/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - React Transition Group</h2>
    <p className={styles.description}>
      Эта библиотека не анимирует стили самостоятельно. Вместо этого, разработчику предоставляется набор простых
      компонентов, которые отслеживают этапы перехода состояния элемента (добавления и удаления в DOM) и управляют
      CSS-стилями, тем самым позволяя применить определенный CSS на каждом этапе.{' '}
    </p>
    <CopyBlock
      text={`npm install react-transition-group`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      <a
        className={styles.link}
        href="http://reactcommunity.org/react-transition-group/"
        target="_blank"
        rel="noopener noreferrer"
      >
        React Transition Group
      </a>
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Компонент Transition</h3>
      <p className={styles.description}>
        Компонент на базе которого построены остальные. Позволяет описывать переход компонента от одного состояния к
        другому с течением времени. По умолчанию компонент <code className={styles.code}>{`<Transition>`}</code> не
        изменяет поведение компонента, который он отображает, а только отслеживает состояния{' '}
        <code className={styles.code}>enter</code> и <code className={styles.code}>exit</code>. Разработчик может
        сделать кастомные эффекты (например, добавляя инлайн стили или классы) под эти состояния.
      </p>
      <p className={styles.description}>Существует 4 основных состояния, в которых может быть элемент: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>entering</code> - элемент появляется в DOM
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>entered</code> - элемент появился в DOM
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>exiting</code> - элемент удаляется из DOM
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>exited</code> - элемент удален из DOM
        </li>
      </ul>
      <p className={styles.description}>Пропы: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>in</code> - буль, сигнализирует о начале стадии появления или удаления элемента.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>timeout</code> - время перехода, кол-во миллисекунд которое будет использовано
          для всех стадий, либо объект с полями под стадии <code className={styles.code}>enter</code> и{' '}
          <code className={styles.code}>exit</code>.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>unmountOnExit</code> - отвечает за удаление элемента из DOM-дерева после фазы{' '}
          <code className={styles.code}>exit</code>.
        </li>
      </ul>
      <p className={styles.description}>
        Если значение пропа <code className={styles.code}>in</code> равно <code className={styles.code}>true</code>,
        компонент начинает стадию <code className={styles.code}>входа</code>. Он переходит из текущего состояния к{' '}
        <code className={styles.code}>entering</code> и останавливается в нем на указанное в{' '}
        <code className={styles.code}>timeout</code> время, по истечении которого переходит в{' '}
        <code className={styles.code}>entered</code>.
      </p>
      <p className={styles.description}>
        Если значение пропа <code className={styles.code}>in</code> равно <code className={styles.code}>false</code>,
        компонент начинает стадию <code className={styles.code}>Выхода</code>. Он переходит из текущего состояния к{' '}
        <code className={styles.code}>exiting</code> и останавливается в нем на указанное в{' '}
        <code className={styles.code}>timeout</code> время, по истечении которого переходит в{' '}
        <code className={styles.code}>exited</code>.
      </p>
      <iframe
        height="300"
        scrolling="no"
        src="https://codesandbox.io/embed/vnjol2wvw0?fontsize=14"
        title="Vanilla CSS transitions"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Компонент CSSTransition</h3>
      <p className={styles.description}>
        Компонент <code className={styles.code}>{`<CSSTransition>`}</code>, в отличии от{' '}
        <code className={styles.code}>{`<Transition>`}</code>, инкапсулирует отслеживание стадии перехода и вызывает
        перекомпоновку (reflow) в браузере, что позволяет без дополнительных усилия реализовать анимациюю добавления
        элемента. Получает те же самые пропы <code className={styles.code}>in</code>,{' '}
        <code className={styles.code}>timeout</code> и другие, а также проп{' '}
        <code className={styles.code}>classNames</code>, в котором можно указать кастомные имена классов для каждой фазы
        перехода.{' '}
      </p>
      <p className={styles.description}>
        Применяет пару CSS-классов для каждой из стадий <code className={styles.code}>appear</code>,{' '}
        <code className={styles.code}>enter</code>, <code className={styles.code}>exit</code> и{' '}
        <code className={styles.code}>done</code> во время перехода. На старте каждой фазы применяется базовый класс,
        после чего сразу применяется класс с приставкой <code className={styles.code}>active</code>. Разработчику
        достаточно передать имя базового класса и описать классы для каждой из стадий.{' '}
      </p>
      <p className={styles.description}>
        К примеру если базовый класс <code className={styles.code}>fade</code>:{' '}
      </p>
      <CopyBlock
        text={`/* Применится как только элемент будет добавлен в DOM */
.fade-enter {
}

/*
* Применится на следующий тик после fade-enter
* Сюда добавляем свойство transition
*/
.fade-enter-active {
}

/* Применится как только проп in получит false */
.fade-exit {
}

/*
* Применится на следующий тик после fade-exit
* Сюда добавляем свойство transition
*/
.fade-exit-active {
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <iframe
        height="300"
        scrolling="no"
        src="https://codesandbox.io/embed/3vz1689l46?fontsize=14"
        title="Vanilla CSS transitions"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.description}>
        Пропу <code className={styles.code}>classNames</code> можно передать объект с кастомными именами классов под
        каждую стадию.{' '}
      </p>
      <CopyBlock
        text={`classNames={{
 appear: 'my-appear',
 appearActive: 'my-active-appear',
 appearDone: 'my-done-appear',
 enter: 'my-enter',
 enterActive: 'my-active-enter',
 enterDone: 'my-done-enter',
 exit: 'my-exit',
 exitActive: 'my-active-exit',
 exitDone: 'my-done-exit',
}}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если используются CSS-модули, то имена классов можно назвать как имена ключей на объекте для{' '}
        <code className={styles.code}>classNames</code> и передать объект классов целиком.
      </p>
      <CopyBlock
        text={`import styles from './styles.module.css';

<CSSTransition classNames={styles}>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <iframe
        height="300"
        scrolling="no"
        src="https://codesandbox.io/embed/mox604444y?fontsize=14"
        title="Vanilla CSS transitions"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Transition events</h3>
      <p className={styles.description}>
        Всего 6 штук, происходят в указанном порядке. Используются для управления сложными анимациями и выполнения кода
        на определенной стадии.{' '}
      </p>
      <CopyBlock
        text={`<CSSTransition
  onEnter={() => console.log('onEnter event')}
  onEntering={() => console.log('onEntering event')}
  onEntered={() => console.log('onEntered event')}
  onExit={() => console.log('onExit event')}
  onExiting={() => console.log('onExiting event')}
  onExited={() => console.log('onExited event')}
/>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Компонент TransitionGroup</h3>
      <p className={styles.description}>
        Компонент <code className={styles.code}>{`<TransitionGroup>`}</code> используется как контейнер для управления
        коллекцией компонентов <code className={styles.code}>{`<Transition>`}</code> или{' '}
        <code className={styles.code}>{`<CSSTransition>`}</code>. При удалении и добавлении элементов коллекции,{' '}
        <code className={styles.code}>{`<TransitionGroup>`}</code> автоматически указывает значение пропа{' '}
        <code className={styles.code}>in</code> добавляемого или удаляемого элемента.{' '}
      </p>
      <p className={styles.description}>
        Принимает проп <code className={styles.code}>component</code>, в котором можно указать тип HTML-элемента который
        необходимо отрендерить как контейнер, по умолчанию будет <code className={styles.code}>div</code>. Для передачи
        стилей есть проп <code className={styles.code}>className</code>, значение которого передается элементу.{' '}
      </p>
      <iframe
        height="300"
        scrolling="no"
        src="https://codesandbox.io/embed/7zn06zzkn1?fontsize=14"
        title="Vanilla CSS transitions"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
  </section>
))
